import {
  AwardTypeCode,
  RoleKey,
  type GettingStartedStep,
  type WaitingItem as ServerWaitingItem,
} from '@myrewards/contracts';
import { QUESTION_KIND_WORDS } from '@myrewards/domain';
import { ROLE_LABEL } from '../session/presenter';
import type { BellItem } from '../shell';
import {
  daysBetween,
  formatCount,
  formatMoney,
  formatShortDate,
  organizationDate,
  plural,
  type IconName,
  type WaitingItem,
} from '../ui';
import { AWARD_TYPE_LABEL } from './labels';
import { MANAGE_PAGE, spotAppointHref } from '../manage/pages';
import { QUEUE_VIEW } from '../ops/labels';

type AwardRef = Extract<ServerWaitingItem, { award: unknown }>['award'];

/** "Due Oct 21 · 7 days left", "Due today · Oct 21", "Past due · Oct 21". */
export function dueLabel(due: string, asOf: string): string {
  const days = daysBetween(asOf, due);
  const date = formatShortDate(due);
  if (days < 0) return `Past due · ${date}`;
  if (days === 0) return `Due today · ${date}`;
  return `Due ${date} · ${plural(days, 'day', 'days')} left`;
}

function awardName(award: AwardRef): string {
  return award.displayId ?? `This ${AWARD_TYPE_LABEL[award.type]}`;
}

/** "$4,500", "$10,000 + 64 options", "64 options". */
function amount(cashCents: number, options: number): string {
  const parts: string[] = [];
  if (cashCents > 0 || options === 0) parts.push(formatMoney(cashCents));
  if (options > 0) parts.push(plural(options, 'option', 'options'));
  return parts.join(' + ');
}

function summary(award: AwardRef): string {
  return [
    AWARD_TYPE_LABEL[award.type],
    plural(award.people, 'person', 'people'),
    amount(award.cashCents, award.options),
  ].join(' · ');
}

/** The award record (G1): what someone opens to change an award sent back to them. */
function awardHref(award: AwardRef): string {
  return `/awards/${award.id}`;
}

/** The decision screen (SL7, X2, X3, R3): every approval and recorded decision goes there. */
function approvalHref(award: AwardRef): string {
  return `/approvals/${award.id}`;
}

type HeldItem = Extract<ServerWaitingItem, { type: 'acted_for_held' }>;

/**
 * APP-148 D17: an award held by "Not what you asked?". The System Admins and the Stock Admin team
 * see what's held and that nothing moves until it's settled; whoever settles it (any Executive,
 * or the award's Sector Lead) is asked to, in their own words when they asked it themselves.
 * Every row opens the award, where it's settled. `meId`: who is reading.
 */
function heldWords(item: HeldItem, meId: string | null): Words {
  const a = item.award;
  const what = `${QUESTION_KIND_WORDS[item.kind]} · ${summary(a)}`;
  const asker = item.questionedBy.id === meId ? null : item.questionedBy.name;
  const said =
    asker === null ? 'you said it isn’t what you asked' : `${asker} says it isn’t what was asked`;
  if (item.role === 'stock_admin' || item.role === 'system_admin')
    return {
      kind: 'task',
      title: `${awardName(a)} is held: ${said}`,
      detail: `${what}. Nobody can claim it, check it or send its letters until it’s settled.`,
      action: { label: 'Open', href: awardHref(a) },
      icon: 'lock',
      problem: 'Held',
    };
  const settler =
    item.role === 'sector_lead'
      ? 'You settle it'
      : asker === null
        ? 'You or any other Executive settles it'
        : 'Any Executive settles it';
  return {
    kind: 'decision',
    title: `Settle ${awardName(a)}: ${said}`,
    detail: `${what}. ${settler}: confirm it as it is, or return it to be changed.`,
    action: { label: 'Review', href: awardHref(a) },
    icon: 'lock',
    problem: 'Held',
  };
}

type SpotReview = Extract<ServerWaitingItem, { type: 'spot_committee_review' }>;

/**
 * APP-240: what a Spot row leads its detail with: the nominator's Title (Q26's letter line) and the
 * Areté value, either of which may be missing (Q9), then the rest, capitalised when it starts.
 */
function spotLead(n: SpotReview['nomination'], rest: string): string {
  const lead = [n.title === null || n.title === undefined ? null : `“${n.title}”`, n.value]
    .filter((part): part is string => part !== null)
    .join(' · ');
  return lead === '' ? `${rest.charAt(0).toUpperCase()}${rest.slice(1)}` : `${lead} · ${rest}`;
}

/**
 * APP-232: a Spot nomination waiting for the committee. "Review a Spot nomination for Ben
 * Adeyemi", "… for Ben Adeyemi and Priya Shah", "… for Ben Adeyemi, Priya Shah and 2 others".
 * It names only colleagues still waiting for the committee, and opens the committee's review.
 */
function spotReviewWords(item: SpotReview): Words {
  const names = item.nominees.map((p) => p.name);
  const more = item.waiting - names.length;
  return {
    kind: 'decision',
    title: `Review a Spot nomination for ${joinNames(more > 0 ? [...names, plural(more, 'other', 'others')] : names)}`,
    detail: spotLead(
      item.nomination,
      `sent ${formatShortDate(item.nomination.sentAt)}. The Spot Committee reviews each colleague, then their manager decides.`,
    ),
    action: { label: 'Review', href: `/recognize/spot/${item.nomination.id}` },
    icon: 'award',
  };
}

type SpotDecision = Extract<ServerWaitingItem, { type: 'spot_supervisor_decision' }>;

/**
 * APP-232: a colleague's Spot award waiting for this person's decision as their supervisor. It
 * opens the decision, under Supervisor decisions.
 */
function spotDecisionWords(item: SpotDecision): Words {
  return {
    kind: 'decision',
    title: `Decide ${item.nominee.name}’s Spot Award · ${formatMoney(item.cashCents)}`,
    detail: spotLead(
      item.nomination,
      `award date ${formatShortDate(item.awardDate)}. The Spot Committee recommended it; approve or veto it as their manager.`,
    ),
    action: {
      label: 'Decide',
      href: `/recognize/spot/${item.nomination.id}/supervision/${item.nominee.id}`,
    },
    icon: 'award',
  };
}

type SpotNeeded = Extract<ServerWaitingItem, { type: 'spot_supervisor_needed' }>;

/**
 * APP-232: a colleague's Spot award with no manager to decide it, for the Stock Admin team to
 * appoint a supervisor (APP-227). It opens that appointment.
 */
function spotNeededWords(item: SpotNeeded): Words {
  return {
    kind: 'task',
    title: `Appoint a supervisor for ${item.nominee.name}’s Spot Award`,
    detail: spotLead(
      item.nomination,
      'the Spot Committee recommended it. No manager is set for them, so it waits until someone can decide as their supervisor.',
    ),
    // APP-235: Spot awards at "No manager set", with the appoint form open for this colleague.
    action: {
      label: 'Appoint a supervisor',
      href: spotAppointHref({ nominationId: item.nomination.id, personId: item.nominee.id }),
    },
    icon: 'people',
    problem: 'No manager set',
  };
}

/** Each getting-started step's name, on Home, in the bell and in My preferences (K11, K25). */
export const GETTING_STARTED_TITLE: Record<GettingStartedStep, string> = {
  preferences: 'Set up your preferences',
  training: 'Take the 10-minute MyRewards training',
};

interface Words {
  kind: WaitingItem['kind'];
  title: string;
  detail: string;
  action: WaitingItem['action'];
  /**
   * What the row is about, never a tick (a tick reads as done; QA-031): the award ribbon for a
   * decision, a warning for an overage, a pencil for a decision to record.
   */
  icon: IconName;
  /** The red pill's words when something's wrong with what's asked ("Over budget"). */
  problem?: string;
}

/** "Dana Park", "Dana Park and J. Moore", "Dana Park, J. Moore and S. Quinn". */
function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] ?? ''}`;
}

/** Who an award is for: up to three names, then "and 2 others"; else how many people. */
function forWhom(award: AwardRef): string {
  const names = (award.recipients ?? []).map((p) => p.name).filter((n) => n !== '');
  if (names.length === 0) return plural(award.people, 'person', 'people');
  const more = award.people - names.length;
  return joinNames(more > 0 ? [...names, plural(more, 'other', 'others')] : names);
}

type BudgetItem = Extract<ServerWaitingItem, { type: 'set_budgets' }>;

/** What already counts against a pool with no budget, by kind of pool. */
const COUNTS_AGAINST: Record<BudgetItem['pools'][number]['kind'], string> = {
  executive: 'Executive Discretionary Bonuses',
  sector: 'Sector Discretionary Bonuses',
  paa_taa: 'PAAs and TAAs',
  spot: 'Spot Awards',
};

/** The Executives' budgets to set this year (X1, FR3): one row, however many pools. */
function budgetWords(item: BudgetItem): Words {
  const fy = String(item.fiscalYear);
  const names = item.pools.map((p) => p.pool.name);
  const only = item.pools.length === 1 ? item.pools[0] : undefined;
  // Each sets budgets where their role keeps them: Executive › Budget pools, or Settings › Budgets.
  const href = item.role === 'executive' ? '/manage/executive/budget-pools' : '/settings/budgets';
  const action = { label: 'Set the budgets', href };
  if (item.allPools)
    return {
      kind: 'decision',
      title: `Set the ${fy} budget pools`,
      detail: `${plural(names.length, 'pool', 'pools')}: ${joinNames(names)}. Each one starts counting as soon as it has a budget.`,
      action: { ...action, label: 'Set all budget pools' },
      icon: 'dollar',
    };
  if (only !== undefined)
    return {
      kind: 'decision',
      title: `Set the ${fy} ${only.pool.name} budget`,
      detail:
        only.usedCashCents > 0
          ? `${COUNTS_AGAINST[only.kind]} already count against this pool: ${formatMoney(only.usedCashCents)} so far in ${fy}.`
          : 'It starts counting as soon as it has a budget.',
      action: { ...action, label: `Set ${only.pool.name} budget` },
      icon: 'dollar',
    };
  return {
    kind: 'decision',
    title: `Set the ${fy} budgets for ${joinNames(names)}`,
    detail: 'Each pool starts counting as soon as it has a budget.',
    action,
    icon: 'dollar',
  };
}

/**
 * APP-104 §6: new feedback for a System Admin or Support person. The count is the reports still
 * New, `since` the oldest one's date; the row opens the list filtered to New.
 */
function feedbackWords(n: number, since: string): Words {
  return {
    kind: 'task',
    title:
      n === 1
        ? 'Review 1 new piece of feedback'
        : `Review ${formatCount(n)} new pieces of feedback`,
    detail: `${n === 1 ? 'It came' : 'The oldest came'} in on ${formatShortDate(since)}. People sent ${n === 1 ? 'it' : 'them'} with the Feedback button, with the page they were on.`,
    action: { label: 'Open feedback', href: '/settings/system/feedback?status=new' },
    icon: 'message',
  };
}

type FeedbackTask = Extract<ServerWaitingItem, { type: 'system_admin_task' | 'support_task' }>;

/** How a sender's answer arrived, as the row says it. */
const ANSWERED: Record<NonNullable<FeedbackTask['one']>['channel'], string> = {
  app: 'in MyRewards',
  email: 'by email',
  teams: 'in Teams',
};

/**
 * APP-138: senders' replies the team hasn't read. One report: "Read 1 reply on FB-2026-0014" (or
 * "Read 2 replies on …"), "Daniel Cho answered by email · ‹title›", opening that report. More:
 * the count of reports, opening the list's Replies to read.
 */
function repliesWords(item: FeedbackTask): Words {
  const one = item.one;
  if (one !== undefined)
    return {
      kind: 'task',
      title: `Read ${plural(one.replies, 'reply', 'replies')} on ${one.displayId}`,
      detail: [`${one.from} answered ${ANSWERED[one.channel]}`, one.title]
        .filter((part) => part !== '')
        .join(' · '),
      action: { label: 'Read reply', href: `/settings/system/feedback/${one.displayId}` },
      icon: 'message',
    };
  return {
    kind: 'task',
    title: `Read replies on ${formatCount(item.count)} pieces of feedback`,
    detail: `The oldest came in on ${formatShortDate(item.since)}.`,
    action: { label: 'Open replies', href: '/settings/system/feedback?status=replies' },
    icon: 'message',
  };
}

type AdminTask = Extract<ServerWaitingItem, { type: 'system_admin_task' }>;

/**
 * A System Admin's work (SYS1, QA-040): where the console and its pages already show it. A stuck
 * claim opens the console's release tool; the rest open the page that fixes them.
 */
function adminTaskWords(item: AdminTask): Words {
  const n = item.count;
  switch (item.task) {
    case 'stuck_claims':
      return {
        kind: 'task',
        title: n === 1 ? 'Release a stuck claim' : `Release ${formatCount(n)} stuck claims`,
        detail:
          'An award’s check or a Shareworks row with no activity past the claim timeout. Release it with a reason so anyone on the Stock Admin team can carry on.',
        action: {
          label: n === 1 ? 'Review the claim' : 'Review the claims',
          href: '/settings/system#stuck-claims',
        },
        icon: 'lock',
      };
    case 'needs_setup':
      return {
        kind: 'task',
        title: n === 1 ? 'Set up 1 person' : `Set up ${formatCount(n)} people`,
        detail:
          'Missing a sector, home org or OH/G&A, so they can’t be added to an award until it’s set.',
        action: { label: 'Open Needs setup', href: '/settings/system/people?status=needs_setup' },
        icon: 'people',
      };
    case 'discrepancies':
      return {
        kind: 'task',
        title:
          n === 1
            ? 'Review a directory discrepancy'
            : `Review ${formatCount(n)} directory discrepancies`,
        detail:
          'A value set in MyRewards that differs from Entra ID, or two records that look like the same person.',
        action: { label: 'Open the directory', href: '/settings/system/directory' },
        icon: 'refresh',
      };
    case 'feedback_to_review':
      return feedbackWords(item.count, item.since);
    case 'feedback_replies_to_read':
      return repliesWords(item);
    case 'go_live':
      return {
        kind: 'task',
        title: `Finish the go-live checklist · ${plural(n, 'required step', 'required steps')} left`,
        detail: 'MyRewards can go live once every required step is done.',
        action: { label: 'Open the checklist', href: '/settings/system/go-live' },
        icon: 'calendarCheck',
      };
  }
}

/**
 * The words for one waiting item. The server sends typed facts; Home and the bell both read them
 * through here, so they always say the same thing. `meId`: who is reading, where a row says "you".
 */
export function waitingWords(item: ServerWaitingItem, meId: string | null = null): Words {
  switch (item.type) {
    case 'payroll_queue':
      return {
        kind: 'decision',
        title: 'Payroll work is waiting',
        detail: [
          item.toPay > 0 ? `${plural(item.toPay, 'line', 'lines')} to pay` : null,
          item.statementsDue > 0
            ? `${plural(item.statementsDue, 'statement', 'statements')} to attach`
            : null,
        ]
          .filter(Boolean)
          .join(' · '),
        action: { label: 'Open payroll queue', href: MANAGE_PAGE.payroll.href },
        icon: 'file',
      };
    case 'make_election':
      return {
        kind: 'decision',
        title: 'Choose how to receive your award',
        detail: [
          item.reward.displayId ?? AWARD_TYPE_LABEL[item.reward.type],
          item.reward.title,
          `Due ${formatShortDate(item.deadline.date)}`,
        ]
          .filter((part): part is string => Boolean(part))
          .join(' · '),
        action: { label: 'Make election', href: `/my-rewards/${item.lineId}` },
        icon: 'award',
      };
    case 'approve_delegate_entry': {
      const a = item.award;
      return {
        kind: 'decision',
        title: `Approve ${awardName(a)} · ${summary(a)}`,
        detail: [
          `Entered by ${a.enteredBy.name}${a.enteredFor ? ' for you' : ''}`,
          a.program?.name,
          item.withinBudget ? 'within budget' : 'over budget',
        ]
          .filter((part): part is string => Boolean(part))
          .join(' · '),
        action: { label: 'Review', href: approvalHref(a) },
        icon: item.withinBudget ? 'award' : 'alert',
        ...(!item.withinBudget && { problem: 'Over budget' }),
      };
    }
    case 'executive_decision': {
      const a = item.award;
      return {
        kind: 'decision',
        title: `Decide ${awardName(a)} · ${summary(a)}`,
        detail: [`Entered by ${a.enteredBy.name}`, a.sector?.name, a.program?.name]
          .filter((part): part is string => Boolean(part))
          .join(' · '),
        action: { label: 'Decide', href: approvalHref(a) },
        icon: 'award',
      };
    }
    case 'approve_overage': {
      const a = item.award;
      if (item.shortfall === null || item.pool === null)
        return {
          kind: 'decision',
          title: `Decide on ${awardName(a)} · the budget now covers it`,
          detail: `Entered by ${a.enteredBy.name} · ${summary(a)}${a.program ? ` · ${a.program.name}` : ''}. A budget added since it was sent covers it; it still needs an Executive’s decision.`,
          action: { label: 'Review', href: approvalHref(a) },
          icon: 'award',
        };
      const short = amount(item.shortfall.cashCents, item.shortfall.options);
      return {
        kind: 'decision',
        title: `Approve a top-up · ${awardName(a)} · ${item.pool.name} is ${short} over budget`,
        detail: `Entered by ${a.enteredBy.name} · ${summary(a)}${a.program ? ` · ${a.program.name}` : ''}. Any one Executive can approve; the top-up defaults to ${short}.`,
        action: { label: 'Review', href: approvalHref(a) },
        icon: 'alert',
        problem: 'Over budget',
      };
    }
    case 'record_decision': {
      const a = item.award;
      // The requester's own request names who it's for, not themselves (QA-031); the Stock
      // Admin team also sees who asked.
      if (item.role === 'requester' || item.role === 'executive_delegate')
        return {
          kind: 'decision',
          title: `Record what the Executives approve for ${forWhom(a)}`,
          detail: `${awardName(a)} · ${summary(a)}. When the Executives reply, enter what they approved and attach the proof.`,
          action: { label: 'Record the decision', href: approvalHref(a) },
          icon: 'pencil',
        };
      return {
        kind: 'task',
        title: `Record the Executives’ decision · ${awardName(a)}`,
        detail: `${summary(a)} for ${forWhom(a)} · requested by ${a.enteredBy.name}. The Executives decide outside MyRewards; record what they decided.`,
        action: { label: 'Record the decision', href: approvalHref(a) },
        icon: 'pencil',
      };
    }
    case 'returned_to_you': {
      const a = item.award;
      return {
        kind: 'task',
        title: `${awardName(a)} was sent back to you`,
        detail: item.reason
          ? `“${item.reason}” · ${summary(a)}`
          : `${summary(a)}. Open it to see what needs changing.`,
        action: { label: 'Open', href: awardHref(a) },
        icon: 'arrowLeft',
      };
    }
    case 'acted_for_held':
      return heldWords(item, meId);
    case 'letters_to_hand_out':
      // APP-245 D7: the letters Letters to hand out lists as not handed out yet, by its count.
      return {
        kind: 'task',
        title:
          item.count === 1
            ? '1 letter to hand out'
            : `${formatCount(item.count)} letters to hand out`,
        detail:
          'Hand each one over in person, or send it once the employee can see their award, and mark it handed out.',
        action: {
          label: 'Open letters to hand out',
          href: MANAGE_PAGE['letters-to-hand-out'].href,
        },
        icon: 'download',
      };
    case 'stock_admin_queue':
      return {
        kind: 'task',
        title:
          item.count === 1
            ? '1 approved award is waiting for a claim'
            : `${formatCount(item.count)} approved awards are waiting for a claim`,
        detail: 'In the Stock Admin queue, oldest first. Your claims show to the whole team.',
        action: { label: 'Open the queue', href: QUEUE_VIEW.queue.href },
        icon: 'clock',
      };
    case 'stock_admin_tax':
      // APP-101: each PAA person's taxes from ADP Quick Calc, before the letters.
      return {
        kind: 'task',
        title: `Tax to enter for ${plural(item.count, 'person', 'people')} on ${plural(item.awards, 'PAA', 'PAAs')}`,
        detail:
          'From ADP Quick Calc, oldest approval first. The letters wait until everyone has one.',
        action: { label: 'Enter the tax', href: QUEUE_VIEW.tax.href },
        icon: 'clock',
      };
    case 'set_budgets':
      return budgetWords(item);
    case 'spot_committee_review':
      return spotReviewWords(item);
    case 'spot_supervisor_decision':
      return spotDecisionWords(item);
    case 'spot_supervisor_needed':
      return spotNeededWords(item);
    case 'system_admin_task':
      return adminTaskWords(item);
    case 'support_task':
      return item.task === 'feedback_replies_to_read'
        ? repliesWords(item)
        : feedbackWords(item.count, item.since);
    case 'getting_started':
      return item.step === 'training'
        ? {
            kind: 'getting-started',
            title: GETTING_STARTED_TITLE.training,
            detail:
              'Five 2-minute lessons, a clickable walkthrough with sample data, and three quick questions. Leave any time and we’ll keep your place.',
            action: { label: 'Start', href: '/training' },
            icon: 'book',
          }
        : {
            kind: 'getting-started',
            title: GETTING_STARTED_TITLE.preferences,
            detail:
              'First-time setup: the name we greet you by, your Home header (Full or Slim), motion and text size. About 2 minutes.',
            action: { label: 'Set up', href: '/settings/me' },
            icon: 'sliders',
          };
  }
}

/**
 * Which group a row sorts in (APP-054 decision 13): held awards (-1), then live work (0); then
 * setup, the year's budgets and the go-live steps left (1) before the getting-started steps (2).
 * Oldest first within each group.
 */
export function waitingGroup(item: ServerWaitingItem): number {
  // A held award tops the list (APP-148 D17): it stops an award until someone settles it.
  if (item.type === 'acted_for_held') return -1;
  if (item.type === 'set_budgets') return 1;
  if (item.type === 'system_admin_task' && item.task === 'go_live') return 1;
  if (item.type === 'getting_started') return 2;
  return 0;
}

/**
 * How long a row about an award has waited (APP-149 D2): "Waiting since Oct 9 · 5 days", or
 * "Waiting since Oct 14 · today". A row that counts several awards says the oldest: "Oldest
 * waiting since Oct 9 · 5 days". The date is the row's `since`, the bell's own, in the
 * organization's calendar, and the days are counted to Home's `asOf`. Rows about an award are
 * the decisions, approvals and top-ups, a decision to record, an award sent back to fix and
 * resend, an award held by "Not what you asked?" (since it was questioned, APP-148 D17), and the
 * queue's claims, tax and Payroll's work; a getting-started step, a setup step,
 * a System Admin's or Support's task and an employee's own choice (it has its due date) say none.
 */
export function waitingSinceWords(item: ServerWaitingItem, asOf: string): string | null {
  // APP-232: each Spot row is one nomination or one colleague, so it never says "Oldest".
  const several = (() => {
    switch (item.type) {
      case 'approve_delegate_entry':
      case 'executive_decision':
      case 'approve_overage':
      case 'record_decision':
      case 'returned_to_you':
      case 'acted_for_held':
      case 'spot_committee_review':
      case 'spot_supervisor_decision':
      case 'spot_supervisor_needed':
        return false;
      case 'stock_admin_queue':
      case 'letters_to_hand_out':
        return item.count > 1;
      case 'stock_admin_tax':
        return item.awards > 1;
      case 'payroll_queue':
        return item.toPay + item.statementsDue > 1;
      case 'make_election':
      case 'set_budgets':
      case 'getting_started':
      case 'system_admin_task':
      case 'support_task':
        return null;
    }
  })();
  if (several === null) return null;
  const days = Math.max(0, daysBetween(organizationDate(item.since), asOf));
  const since = `${several ? 'Oldest waiting' : 'Waiting'} since ${formatShortDate(item.since)}`;
  return `${since} · ${days === 0 ? 'today' : plural(days, 'day', 'days')}`;
}

/**
 * Where a row goes By role (APP-149 D6): the elevated roles in the app's role order, then
 * Employee, everyone's own role, last. A row from two roles goes with its first.
 */
function roleOrder(item: ServerWaitingItem): number {
  return item.role === 'employee' ? RoleKey.options.length : RoleKey.options.indexOf(item.role);
}

/** Where a row goes By award type (D6), in the award types' own order; null when it has none. */
function awardTypeOrder(item: ServerWaitingItem): number | null {
  if ('award' in item) return AwardTypeCode.options.indexOf(item.award.type);
  // An employee's choice and the tax to enter are both PAAs.
  if (item.type === 'make_election' || item.type === 'stock_admin_tax')
    return AwardTypeCode.options.indexOf('PAA');
  if (
    item.type === 'spot_committee_review' ||
    item.type === 'spot_supervisor_decision' ||
    item.type === 'spot_supervisor_needed'
  )
    // A Spot Award comes after the four entry types, as everywhere they're listed.
    return AwardTypeCode.options.length;
  return null;
}

/**
 * One Home Waiting on you row; `asOf` is Home's day, for how long it has waited; `meId` is who
 * is reading.
 */
export function toWaitingItem(
  item: ServerWaitingItem,
  asOf: string,
  meId: string | null = null,
): WaitingItem {
  const words = waitingWords(item, meId);
  const since = waitingSinceWords(item, asOf);
  const type = awardTypeOrder(item);
  return {
    id: item.id,
    role: ROLE_LABEL[item.role],
    // One row for someone who is both a System Admin and Support carries both tags.
    ...(item.type === 'system_admin_task' &&
      item.roles !== undefined && { roleTags: item.roles.map((r) => ROLE_LABEL[r]) }),
    roleTone: item.role === 'employee' ? 'neutral' : 'info',
    kind: words.kind,
    title: words.title,
    detail: words.detail,
    since: item.since,
    ...(since !== null && { waitingSince: since }),
    roleOrder: roleOrder(item),
    ...(type !== null && { awardTypeOrder: type }),
    action: words.action,
    icon: words.icon,
    group: waitingGroup(item),
    ...(words.problem !== undefined && { problem: words.problem }),
    // Getting-started steps count until done or dismissed (K25); My preferences shows them again.
    ...(item.type === 'getting_started' && { dismissible: true }),
  };
}

/** The getting-started step a Waiting on you row is, or `null` for any other row. */
export function gettingStartedStep(id: string, items: readonly ServerWaitingItem[]) {
  const item = items.find((i) => i.id === id);
  return item?.type === 'getting_started' ? item.step : null;
}

/**
 * The bell's "Getting started": the setup rows of Waiting on you (the year's budgets, then the
 * getting-started steps), in its order and words, so the bell counts them too.
 */
export function bellSetupItems(items: readonly ServerWaitingItem[]): BellItem[] {
  return items
    .filter((item) => waitingGroup(item) > 0)
    .sort((a, b) => waitingGroup(a) - waitingGroup(b) || a.since.localeCompare(b.since))
    .map(toBellItem);
}

/** APP-232: the Spot rows of Waiting on you, which the bell counts while they wait. */
const SPOT_ROWS: ReadonlySet<ServerWaitingItem['type']> = new Set([
  'spot_committee_review',
  'spot_supervisor_decision',
  'spot_supervisor_needed',
]);

/**
 * The bell's "Waiting on you", oldest first: live work no notice announces, a System Admin's tasks
 * (QA-040, APP-044 §32), which come about by time passing or a sync; and (APP-232) the Spot rows,
 * which wait on the committee, a supervisor or the Stock Admin team until someone acts, whether or
 * not its notice has been read. Other roles' live work reaches the bell as notices.
 */
export function bellWaitingItems(items: readonly ServerWaitingItem[]): BellItem[] {
  return items
    .filter(
      (item) =>
        (item.type === 'system_admin_task' ||
          item.type === 'support_task' ||
          SPOT_ROWS.has(item.type)) &&
        waitingGroup(item) === 0,
    )
    .sort((a, b) => a.since.localeCompare(b.since))
    .map(toBellItem);
}

/** The bell lists the same things as Waiting on you, in the same words. */
export function toBellItem(item: ServerWaitingItem): BellItem {
  const words = waitingWords(item);
  return {
    id: item.id,
    title: words.title,
    meta: `Since ${formatShortDate(item.since)}`,
    role: ROLE_LABEL[item.role],
    roleTone: item.role === 'employee' ? 'neutral' : 'info',
    href: words.action.href,
  };
}
